import { useRouter } from 'vue-router';
import { vragenStore } from '@/stores/vragen.js';
import { formStore } from '@/stores/form.js';
import { verzekeringStore } from "@/stores/verzekering.js";

export function useRouteExceptions() {
  const router = useRouter();
  let routesAreUpdated = false;

  /**
   * Dynamically updates routes based on the active funnel type.
   */
  const updateRoutesForFunnel = async () => {
    const form = formStore();
    const vragen = vragenStore();
    const funnelType = form.funnelType;

    // Exit if no active funnel
    if (!funnelType) return;

    // Dynamically import the correct routes file based on the active funnelType
    const { default: funnelRoutes } = await import(`@/router/funnels/routes.${funnelType}.js`);

    // Run the correct exception handler based on funnelType
    switch (funnelType) {
      case 'boot':
        routesAreUpdated = true;
        handleBootRoutes(router, vragen, funnelRoutes);
        break;
      default:
        break;
    }
  };

  /**
   * Handles route exceptions for the 'boot' funnel.
   */
  const handleBootRoutes = (router, vragen, funnelRoutes) => {
    const verzekering = verzekeringStore();

    const dinghyEnabled = vragen.voertuig?.VolgbootMeeverzekeren === '1';
    const trailerEnabled = vragen.voertuig?.TrailerMeeverzekeren === '1' && verzekering.getDekking('Voertuig') === 'vc';

    // List of routes to check, each with { name, enabled }
    // True if the route should be added, false if it should be removed
    const routesToCheck = [
      { name: 'gegevens-volgboot', enabled: dinghyEnabled },
      { name: 'gegevens-trailer', enabled: trailerEnabled },
    ];

    applyRouteChanges(router, funnelRoutes, routesToCheck);
  };

  /**
   * Loops through routesToCheck and adds or removes routes dynamically based on conditions.
   * @param router - Vue Router instance
   * @param funnelRoutes - The full list of routes for the active funnel
   * @param routesToCheck - List of routes to check, each with { name, enabled }
   */
  const applyRouteChanges = (router, funnelRoutes, routesToCheck) => {
    const form = formStore();

    routesToCheck.forEach(({ name, enabled }) => {
      const existingRoute = router.getRoutes().find((r) => r.name === name);

      if (enabled && !existingRoute) {
        const originalRoute = funnelRoutes.routes.find((r) => r.name === name);
        if (originalRoute) {
          insertRouteAt(router, originalRoute, funnelRoutes);
        }
      } else if (!enabled && existingRoute) {
        router.removeRoute(name);
      }
    });

    // After all routes have been added/removed, we add the routes to the form.sortedRoutes array
    // This is necessary for the submit/back button to work correctly
    // We only do this if there is an exception handler for the active funnel
    if (routesAreUpdated) {
      form.sortedRoutes = router.getRoutes().map((r) => r.name);
    }
  };

  /**
   * Inserts a route at the correct position in the router based on the index.
   * @param router - Vue Router instance
   * @param route - Route to insert
   * @param funnelRoutes - The full list of routes for this specific funnel
   */
  const insertRouteAt = (router, route, funnelRoutes) => {
    const index = funnelRoutes.routes.findIndex((r) => r.name === route.name);
    // Exit if the route is not found in the funnel routes
    if (index === -1) return;

    // Filter out only the routes that exist in bootRoutes.routes (ensuring we don’t remove system-defined routes).
    // Vue Router does not support inserting a route at a specific position, so we remove and re-add all routes to guarantee the correct order.
    // Then loop over those routes and remove them
    const funnelRelatedRoutes = router.getRoutes().filter((r) => funnelRoutes.routes.some((fr) => fr.name === r.name));
    funnelRelatedRoutes.forEach((r) => router.removeRoute(r.name));

    // Loop through all routes in the funnel.
    // When we reach index, we insert the new route
    // If a route from funnelRelatedRoutes (previously removed) is in funnelRoutes, we re-add it.
    funnelRoutes.routes.forEach((r, i) => {
      if (i === index) {
        router.addRoute(route); // Add the missing route in the right place
      }
      if (funnelRelatedRoutes.some((fr) => fr.name === r.name)) {
        router.addRoute(r); // Re-add existing routes
      }
    });
  };

  return { updateRoutesForFunnel };
}
